
import { z } from 'zod'
import { AvailableType } from '../rates/rate.types'
import { Status } from '@/core/types/utils'
import {
  AgentStatus,
  AgentType,
} from '@/core/components/sales-agents/salesAgent.types'

export enum PromoCodeType {
  'percentageOff' = 'percentageOff',
  'fixedDiscount' = 'fixedDiscount',
  'activeRate' = 'activeRate',
}

export const promoCodeFormSchema = z
  .object({
    type: z.enum(PromoCodeType),
    // 0 is a legitimate discount: a zero-value code is a pure tracking link to
    // a sales agent, so the floor is 0 and not 1.
    value: z
      .transform(Number)
      .pipe(z.number().min(0, { error: 'val.min0' }))
      .optional(),
    availableType: z.enum(AvailableType),
    title: z
      .string({ error: 'val.required' })
      .min(2, { error: 'val.required' })
      .max(100, { error: 'val.max100' }),
    description: z.string().optional(),
    startDate: z.string(),
    endDate: z.string(),
    startTime: z.string(),
    endTime: z.string(),
    code: z
      .string({ error: 'val.required' })
      .min(2)
      .max(36, { error: 'val.max36' }),
    maxUsages: z.transform(Number).pipe(z.number().int()), // 0 = unlimited
    rates: z.array(z.string().optional()),
    activities: z.array(z.string().optional()),
    status: z.enum(Status),
    // 0..1 sales agent per code; null clears the association.
    agentId: z.string().length(16).nullable().optional(),
  })
  .check((ctx) => {
    const { value, issues } = ctx
    if (
      (value.type === PromoCodeType.fixedDiscount ||
        value.type === PromoCodeType.percentageOff) &&
      (value.value === undefined || value.value === null)
    ) {
      issues.push({
        code: 'custom',
        message: 'val.required',
        path: ['value'],
        input: value,
      })
    }

    if (
      value.type === PromoCodeType.percentageOff &&
      value.value !== undefined &&
      value.value !== null &&
      value.value > 100
    ) {
      issues.push({
        code: 'custom',
        message: 'val.max100',
        path: ['value'],
        input: value,
      })
    }

    if (
      value.rates?.length < 1 &&
      value.activities?.length < 1 &&
      value.type !== PromoCodeType['activeRate']
    ) {
      issues.push({
        code: 'custom',
        message: 'val.rateOrActivityRequired',
        path: ['rates'],
        input: value,
      })
      issues.push({
        code: 'custom',
        message: 'val.rateOrActivityRequired',
        path: ['activities'],
        input: value,
      })
    }
  })

export type PromoCodeFormValues = z.infer<typeof promoCodeFormSchema>
export type PromoCodeFormInputValues = z.input<typeof promoCodeFormSchema>

export const promoCodeSchema = z.object({
  type: z.enum(PromoCodeType),
  value: z
    .transform(Number)
    .pipe(z.number().int().min(1, { error: 'val.min1' }).max(100,{error:'val.max100'}))
    .optional(),
  availableType: z.enum(AvailableType),
  title: z.string({ error: 'val.required' }).min(2, { error: 'val.required' }),
  description: z.string().optional(),
  visible: z.boolean({ error: 'val.required' }),
  startDate: z.string(),
  endDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  code: z.string({ error: 'val.required' }),
  maxUsages: z.transform(Number).pipe(z.number().int()), // 0 = unlimited
  rates: z.array(z.object({ itemId: z.string(), name: z.string() }).optional()),
  activities: z.array(
    z.object({ itemId: z.string(), name: z.string() }).optional()
  ),
  id: z.string(),
  status: z.enum(Status),
  agentId: z.string().nullable().optional(),
  // The GET returns the resolved agent alongside the raw id so the UI does not
  // have to cross-reference the agents list to render it.
  agent: z
    .object({
      id: z.string(),
      agentNumber: z.string(),
      type: z.enum(AgentType),
      status: z.enum(AgentStatus),
    })
    .nullable()
    .optional(),
})
export type PromoCode = z.infer<typeof promoCodeSchema>
